/**
 * LandingFeatures - Features grid, products showcase, benefits, and testimonials
 */
import { Link } from 'react-router-dom'
import {
  Globe,
  Shield,
  Zap,
  HeadphonesIcon,
  CreditCard,
  Cloud,
  BadgePercent,
  Clock,
  Package,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'

const features = [
  {
    icon: Globe,
    title: 'Pan-African Coverage',
    description: 'Serving all 54 African countries with local support, currencies, and compliance.',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Protect your business with world-class antivirus, backup, and disaster recovery.',
  },
  {
    icon: Zap,
    title: 'Instant Provisioning',
    description: 'Get your licenses activated within minutes, not days. No waiting around.',
  },
  {
    icon: HeadphonesIcon,
    title: 'Local Support',
    description: 'Real humans who understand your business, in your timezone, speaking your language.',
  },
  {
    icon: CreditCard,
    title: 'Flexible Payments',
    description: 'Pay in your local currency with payment methods that work for African businesses.',
  },
  {
    icon: Cloud,
    title: 'All Major Vendors',
    description: 'Microsoft 365, Google Workspace, antivirus, backup—everything in one place.',
  },
]

const products = [
  {
    name: 'Microsoft 365',
    description: 'Email, Office apps, Teams, and cloud storage for your team',
    icon: '\u{1F4E7}',
    popular: true,
  },
  {
    name: 'Security & Antivirus',
    description: 'Protect your business from cyber threats and ransomware',
    icon: '\u{1F6E1}\uFE0F',
    popular: true,
  },
  {
    name: 'Cloud Backup',
    description: 'Never lose your data. Automatic backups for peace of mind',
    icon: '\u{1F4BE}',
    popular: false,
  },
  {
    name: 'Google Workspace',
    description: 'Gmail, Drive, Docs, and collaboration tools for your team',
    icon: '\u{1F537}',
    popular: false,
  },
]

const benefits = [
  {
    icon: BadgePercent,
    title: 'Competitive Pricing',
    description: 'Better rates than buying direct, with volume discounts for growing teams.',
  },
  {
    icon: Clock,
    title: 'Save Time',
    description: 'One invoice, one portal, one support team for all your software needs.',
  },
  {
    icon: Package,
    title: 'Easy Management',
    description: 'Add or remove licenses instantly. Scale up or down as your business changes.',
  },
]

const testimonials = [
  {
    quote: "Mzanzi made getting Microsoft 365 for our team so easy. Support is excellent and they understand African businesses.",
    author: "Sarah M.",
    company: "TechStart Lagos",
    role: "CEO"
  },
  {
    quote: "Finally, a provider that accepts local payment methods and doesn't charge us in USD. Game changer.",
    author: "John K.",
    company: "Innovate Kenya",
    role: "IT Manager"
  },
]

export function ProductsSection() {
  return (
    <section id="products" className="scroll-mt-16">
      <div className="container px-4 md:px-6 py-16 md:py-24">
        <div className="text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4">Products</Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Everything your business needs
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From email and productivity to security and backup—we&apos;ve got you covered.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product) => (
            <Card key={product.name} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative">
              {product.popular && (
                <div className="absolute -top-2 -right-2">
                  <Badge className="bg-green-500">Popular</Badge>
                </div>
              )}
              <CardHeader className="text-center">
                <div className="text-4xl mb-4">{product.icon}</div>
                <CardTitle className="text-lg">{product.name}</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-sm">
                  {product.description}
                </CardDescription>
                <Button variant="link" className="mt-4" asChild>
                  <Link to="/login">Learn more &rarr;</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}

export function BenefitsSection() {
  return (
    <section className="bg-muted/30">
      <div className="container px-4 md:px-6 py-16 md:py-24">
        <div className="text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4">Why Mzanzi</Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Why African businesses choose us
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary mx-auto mb-4">
                <benefit.icon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
              <p className="text-muted-foreground">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function FeaturesSection() {
  return (
    <section id="features" className="scroll-mt-16">
      <div className="container px-4 md:px-6 py-16 md:py-24">
        <div className="text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4">Features</Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Built for African businesses
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We understand the unique challenges of doing business in Africa.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature) => (
            <Card key={feature.title} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardHeader>
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <feature.icon className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}

export function TestimonialsSection() {
  return (
    <section className="bg-muted/30">
      <div className="container px-4 md:px-6 py-16 md:py-24">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Testimonials</Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            What our customers say
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.author} className="p-6">
              <p className="text-lg mb-4 italic">&ldquo;{testimonial.quote}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center font-semibold text-primary">
                  {testimonial.author.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold">{testimonial.author}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.role}, {testimonial.company}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}
